import { Zap } from 'lucide-react';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { EmissionScope } from '../../types/emissions';
import { ActivitySelectionCard } from '../ActivitySelectionCard';
import {
  DataMethod,
  type FormData,
  SCOPE1_ACTIVITY_TYPES,
  SCOPE2_ACTIVITY_TYPES,
} from './types';

interface ActivityTypeStepProps {
  dataMethod: DataMethod | null;
  formData: Pick<FormData, 'activityType' | 'scope'>;
  onSelect: (activityType: string) => void;
  onSelectScope2Bulk: () => void;
  /**
   * Render without the outer Card chrome and intro copy. Used when this step
   * is embedded inside a modal that already supplies its own header.
   */
  embedded?: boolean;
}

export const ActivityTypeStep = ({ dataMethod, formData, onSelect, onSelectScope2Bulk, embedded = false }: ActivityTypeStepProps) => {
  const body = (
    <div className='tw:space-y-8'>
          <div>
            <p className='tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wider tw:text-muted-foreground tw:mb-4'>
              Scope 1 — Direct Emissions
            </p>
            <div className='tw:grid tw:grid-cols-2 tw:md:grid-cols-6 tw:gap-4'>
              {SCOPE1_ACTIVITY_TYPES.map(({ value, label, description, Icon, iconBg, iconColor }) => (
                <ActivitySelectionCard
                  key={value}
                  Icon={Icon}
                  iconBg={iconBg}
                  iconColor={iconColor}
                  label={label}
                  description={description}
                  isSelected={formData.activityType === value}
                  onClick={() => onSelect(value)}
                />
              ))}
            </div>
          </div>

          <div>
            <p className='tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wider tw:text-muted-foreground tw:mb-4'>
              Scope 2 — Purchased Energy
            </p>
            {dataMethod === DataMethod.BulkUpload ? (
              <div className='tw:grid tw:grid-cols-2 tw:md:grid-cols-6 tw:gap-4'>
                <ActivitySelectionCard
                  Icon={Zap}
                  iconBg='tw:bg-yellow-100'
                  iconColor='tw:text-yellow-600'
                  label='Purchased Energy'
                  description='Electricity, heating, steam, and cooling from the grid or district networks'
                  isSelected={formData.scope === EmissionScope.Scope2 && formData.activityType === ''}
                  onClick={onSelectScope2Bulk}
                />
              </div>
            ) : (
              <div className='tw:grid tw:grid-cols-2 tw:md:grid-cols-6 tw:gap-4'>
                {SCOPE2_ACTIVITY_TYPES.map(({ value, label, description, Icon, iconBg, iconColor }) => (
                  <ActivitySelectionCard
                    key={value}
                    Icon={Icon}
                    iconBg={iconBg}
                    iconColor={iconColor}
                    label={label}
                    description={description}
                    isSelected={formData.activityType === value}
                    onClick={() => onSelect(value)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* <div>
            <p className='tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wider tw:text-muted-foreground tw:mb-4'>
              Scope 3 — Value Chain
            </p>
            <div className='tw:grid tw:grid-cols-2 tw:md:grid-cols-6 tw:gap-4'>
              {SCOPE3_COMING_SOON.map(({ label, description, Icon, iconBg, iconColor }) => (
                <ActivitySelectionCard
                  key={label}
                  Icon={Icon}
                  iconBg={iconBg}
                  iconColor={iconColor}
                  label={label}
                  description={description}
                  comingSoon
                />
              ))}
            </div>
          </div> */}
    </div>
  );

  if (embedded) {
    return body;
  }

  return (
    <Card className='tw:mb-6'>
      <CardContent className='tw:p-6'>
        <h5 className='tw:font-semibold tw:mb-2'>What are you reporting?</h5>
        <p className='tw:text-sm tw:text-muted-foreground tw:mb-6'>
          Select the activity type that best describes this emission source.
        </p>
        {body}
      </CardContent>
    </Card>
  );
};
